import uuid from 'uuid';
import { fromRange, toRange } from 'xpath-range';

import assessmentReader from '../store/readHelpers/AssessmentReader';
import responseReader from '../store/readHelpers/ResponseReader';
import rangyService from './RangyService';
import responseService from './ResponseService';
import studentHighlightsActions from '../actions/StudentHighlightsActions';
import storeManager from '../store/StoreManager';
import sessionReader from '../store/readHelpers/SessionReader';
import settingsReader from '../store/readHelpers/SettingsReader';
import routeTypes from '../constants/RouteTypes';
import marketplacePropertiesService from './MarketplacePropertiesService';

const studentHighlightsService = {
    STUDENT_HIGHLIGHT_CLASS_NAME: 'student-highlight',
    THE_ONE_WE_WANT_ID: 'THE_ONE_WE_WANT',
    HIGHLIGHT_ID_ATTR: 'data-highlight-id',

    getStimulusElement(contentRevisionId) {
        return document.getElementById(`stimulus-content-${contentRevisionId}`);
    },

    getStimulusAndStudentHighlightInfo() {
        const state = storeManager.getState();
        const { currentlyShown } = state;
        const { sectionId } = currentlyShown;
        const section = assessmentReader.getSectionById(sectionId);

        if (!section.stimulusContent) {
            window.console.warn('Student highlight only supported in stimulus content for now');
            return {};
        }

        const { contentRevisionId } = section.stimulusContent;
        const itemResult = responseReader.getItemResultForQuestion(contentRevisionId);

        let studentHighlights = [];
        if (Array.isArray(itemResult.studentHighlights)) {
            studentHighlights = itemResult.studentHighlights.slice(0);
        }

        return {
            contentRevisionId,
            studentHighlights,
        };
    },

    getRangeLimitedToRoot(originalRange, enforcedAncestor) {
        const rangy = rangyService.getRangy();
        const newRange = rangy.createRange(window);
        if (enforcedAncestor && !this.isDescendantOrSame(originalRange.startContainer, enforcedAncestor)) {
            if (enforcedAncestor.firstChild.nodeType === Node.ELEMENT_NODE) {
                newRange.setStart(enforcedAncestor, 0);
            } else {
                newRange.setStart(enforcedAncestor.firstChild, 0);
            }
        } else {
            newRange.setStart(originalRange.startContainer, originalRange.startOffset);
        }

        if (enforcedAncestor && !this.isDescendantOrSame(originalRange.endContainer, enforcedAncestor)) {
            const lastChild = this.getLastChild(enforcedAncestor);
            if (lastChild.nodeType === Node.ELEMENT_NODE) {
                newRange.setEndAfter(lastChild);
            } else {
                newRange.setEnd(lastChild, lastChild.length);
            }
        } else {
            newRange.setEnd(originalRange.endContainer, originalRange.endOffset);
        }

        return newRange;
    },

    createValidRangeFromCurrentSelection(rootNode) {
        const selection = window.getSelection();
        const range = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

        return range ? this.getRangeLimitedToRoot(range, rootNode) : null;
    },

    isDescendantOrSame(node, possibleAncestor) {
        let parent = node;
        while (parent) {
            if (parent === possibleAncestor) {
                return true;
            }
            parent = parent.parentNode;
        }

        return false;
    },

    isChildOfHighlightElement(node) {
        const parent = node.parentNode;
        return parent !== null
            && parent.nodeType === Node.ELEMENT_NODE
            && parent.classList.contains(this.STUDENT_HIGHLIGHT_CLASS_NAME);
    },

    getLastChild(element) {
        return element.childNodes[element.childNodes.length - 1];
    },

    /**
     * classApplier.undoToRange() can sometimes leave highlight spans in the dom, but with the 'student-highlight'
     * class removed. This will lift out the span contents and delete the span.
     *
     * @param rootNode
     */
    cleanUpUnHighlightedSpans(rootNode) {
        const toClean = Array.from(
            rootNode.querySelectorAll(`span:not(.${this.STUDENT_HIGHLIGHT_CLASS_NAME})[${this.HIGHLIGHT_ID_ATTR}]`)
        );

        toClean.forEach((span) => {
            const newParent = span.parentElement;
            const insertAfter = span.previousSibling;
            this.appendContentsFrom(span, newParent, insertAfter);
            span.parentElement.removeChild(span);
            newParent.normalize();
        });
    },

    getAllHighlightRanges(stimulusNode) {
        return this.getAllHighlightIdsInElement(stimulusNode)
            .map((highlightId) => {
                return this.getRangeForHighlightId(highlightId, stimulusNode);
            });
    },

    getRangeForHighlightId(highlightId, stimulusNode) {
        const highlightElements = this.getAllElementsForHighlightId(highlightId, stimulusNode);
        const firstElem = highlightElements[0];
        const lastElem = highlightElements[highlightElements.length - 1];

        this.selectAllElementsForHighlightId(highlightId, stimulusNode);
        const selectedRange = this.createValidRangeFromCurrentSelection(stimulusNode);

        if (!selectedRange) {
            return null;
        }

        if (this.isChildOfHighlightElement(selectedRange.startContainer)) {
            selectedRange.setStartBefore(selectedRange.startContainer.parentElement);
        }
        if (this.isChildOfHighlightElement(selectedRange.endContainer)) {
            selectedRange.setEndAfter(selectedRange.endContainer.parentElement);
        }

        const xpRange = fromRange(selectedRange, stimulusNode);
        xpRange.id = highlightId;

        // Set start
        const firstParentChildren = Array.from(firstElem.parentElement.childNodes);
        if (firstElem.firstChild.nodeType === Node.ELEMENT_NODE) {
            // Start part will be correct in xpRange!
        } else if (firstElem.previousSibling) {
            const startContainerIndex = firstParentChildren.indexOf(firstElem.previousSibling);
            let counter = 0;
            for (let i = 0; i <= startContainerIndex; i++) {
                if (firstParentChildren[i].nodeType === Node.TEXT_NODE) {
                    // We count text nodes
                    counter++;
                }
            }
            xpRange.start = `${xpRange.start}/text()[${counter}]`;
            xpRange.startOffset = firstElem.previousSibling.length;
        } else {
            xpRange.start = `${xpRange.start}/text()[1]`;
            xpRange.startOffset = 1;
        }

        // Set end
        const lastElemChildren = Array.from(lastElem.childNodes);
        const lastChildElem = lastElemChildren[lastElemChildren.length - 1];
        const elementMode = lastChildElem.nodeType === Node.ELEMENT_NODE;
        const helperFragment = this.getHelperFragment(lastElem.parentElement, highlightId, lastChildElem);
        const helperArray = Array.from(helperFragment.childNodes);
        let textNodeCounter = 0;
        let nodeCounter = 0;

        for (nodeCounter; nodeCounter < helperArray.length; nodeCounter++) {
            if (helperArray[nodeCounter].nodeType === Node.TEXT_NODE) {
                textNodeCounter++;
            }
            if (helperArray[nodeCounter].id === this.THE_ONE_WE_WANT_ID) {
                break;
            }
        }

        if (elementMode) {
            // xpRange.end will be correct, but need to find the offset!
            xpRange.endOffset = nodeCounter + 1;
        } else {
            const endPath = this.pruneSpans(xpRange.end, selectedRange.commonAncestorContainer, lastElem, highlightId);
            xpRange.end = `${endPath}/text()[${textNodeCounter}]`;
            xpRange.endOffset = lastElem.previousSibling && lastElem.previousSibling.nodeType === Node.TEXT_NODE
                ? lastElem.previousSibling.length + lastChildElem.length
                : lastChildElem.length;
        }

        if (xpRange.start.length === 0) {
            xpRange.start = '/';
        }
        if (xpRange.end.length === 0) {
            xpRange.end = '/';
        }

        return xpRange;
    },

    /**
     * When highlights are applied as spans in the dom, the xpath to the end of the range is possibly polluted
     * by those spans. This function calculates the xpath to the end of the range, as it should be before the highlight
     * is applied.
     *
     * @param {string} xPath xpath generated while the highlight spans are in place.
     * @param {Element} rootElement From which the xpath is calculated.
     * @param {Node} nestedNode The node to which the xPath points.
     * @param {string} highlightId Identifier of the highlight
     * @return {string} Correct xpath to end of range when highlight is not yet applied.
     */
    pruneSpans(xPath, rootElement, nestedNode, highlightId) {
        const xpArr = xPath.split('/').reverse();
        const newArr = [];
        const pattern = /span\[(\d)\]/i;
        let currentParent = nestedNode.parentElement.parentElement;
        let currentLevel = -1;

        while (currentParent !== rootElement.parentElement && currentLevel < xpArr.length - 1) {
            currentLevel++;
            let xpElm = xpArr[currentLevel];
            const spanCheck = xpElm.match(pattern);
            if (spanCheck) {
                const spanCount = parseInt(spanCheck[1], 10);
                let wantedSpanCount = spanCount;
                let checkedSpanCount = 0;
                for (let i = 0; i < currentParent.childNodes.length; i++) {
                    const node = currentParent.childNodes[i];
                    if (node.nodeName === 'SPAN') {
                        checkedSpanCount++;
                        if (node.getAttribute(this.HIGHLIGHT_ID_ATTR) === `${highlightId}`) {
                            wantedSpanCount--;
                        }

                        if (checkedSpanCount === spanCount) {
                            break;
                        }
                    }
                }

                xpElm = `span[${wantedSpanCount}]`;
            }

            newArr.push(xpElm);
            currentParent = currentParent.parentElement;
        }

        if (newArr.length !== xpArr.length) {
            for (let i = newArr.length; i < xpArr.length; i++) {
                newArr.push(xpArr[i]);
            }
        }

        return newArr.reverse().join('/');
    },

    /**
     * Gets a document fragment representing the state of a highlighted range without the highlight spans in place.
     *
     * @param parent
     * @param highlightId
     * @param lastChildElem
     * @return {DocumentFragment}
     */
    getHelperFragment(parent, highlightId, lastChildElem) {
        const fragment = document.createDocumentFragment();
        let expectedTextNodesCounter = 0;
        let previousNode = { nodeType: -1 };
        let theOneTextNodeIndex;

        Array
            .from(parent.childNodes)
            .forEach((node) => {
                const isHighlightId = node.nodeType === 1 && node.getAttribute(this.HIGHLIGHT_ID_ATTR) === `${highlightId}`;
                if (!isHighlightId) {
                    if (node.nodeType === Node.TEXT_NODE && previousNode.nodeType !== Node.TEXT_NODE) {
                        expectedTextNodesCounter++;
                    }
                    fragment.appendChild(node.cloneNode(false));
                    previousNode = node;

                    return;
                }

                Array
                    .from(node.childNodes)
                    .forEach((hlNode) => {
                        if (hlNode.nodeType === Node.TEXT_NODE && previousNode.nodeType !== Node.TEXT_NODE) {
                            expectedTextNodesCounter++;
                        }
                        const clone = hlNode.cloneNode(false);
                        if (hlNode === lastChildElem && hlNode.nodeType === Node.TEXT_NODE) {
                            theOneTextNodeIndex = expectedTextNodesCounter - 1;
                        } else if (hlNode === lastChildElem) {
                            clone.id = this.THE_ONE_WE_WANT_ID;
                        }

                        fragment.appendChild(clone);
                        previousNode = hlNode;
                    });
            });

        fragment.normalize();
        if (theOneTextNodeIndex !== undefined) {
            const textNodes = Array
                .from(fragment.childNodes)
                .filter(node => node.nodeType === Node.TEXT_NODE);
            textNodes[theOneTextNodeIndex].id = this.THE_ONE_WE_WANT_ID;
        }
        return fragment;
    },

    /**
     * @param {Array} elements
     */
    mergeElements(elements) {
        if (elements.length === 1) {
            return;
        }

        let previousElement;
        elements.reverse().forEach((element) => {
            if (previousElement === element.nextSibling) {
                this.appendContentsFrom(previousElement, element);
                element.parentElement.removeChild(previousElement);
                element.normalize();
            }
            previousElement = element;
        });
    },

    appendContentsFrom(fromElement, toElement, appendAfter) {
        let after = appendAfter;
        Array
            .from(fromElement.childNodes)
            .forEach((node) => {
                if (after && after.nextSibling) {
                    toElement.insertBefore(node, after.nextSibling);
                    after = node;
                } else if (toElement.firstChild === fromElement) {
                    toElement.prepend(node);
                } else {
                    toElement.appendChild(node);
                }
            });
    },

    getAllElementsForHighlightId(highlightId, parentNode) {
        return Array.from(
            parentNode.querySelectorAll(`[${this.HIGHLIGHT_ID_ATTR}="${highlightId}"]`)
        );
    },

    getAllHighlightElements(parentNode) {
        return Array.from(
            parentNode.querySelectorAll(`[${this.HIGHLIGHT_ID_ATTR}]`)
        );
    },

    getAllHighlightIdsInElement(parentNode) {
        const idMap = this.getAllHighlightElements(parentNode)
            .reduce((map, element) => {
                const id = element.getAttribute(this.HIGHLIGHT_ID_ATTR);
                map[id] = true;
                return map;
            }, {});

        return Object.keys(idMap);
    },

    selectAllElementsForHighlightId(highlightId, parentNode) {
        const rangy = rangyService.getRangy();
        const highlightElements = this.getAllElementsForHighlightId(highlightId, parentNode);
        const range = rangy.createRangyRange();
        range.setStartBefore(highlightElements[0]);
        range.setEndAfter(highlightElements[highlightElements.length - 1]);

        const selection = rangy.getSelection();
        selection.setSingleRange(range);
    },

    replaceHighlightIdsInElements(newId, idsToReplace, parentNode) {
        idsToReplace.forEach((id) => {
            this.getAllElementsForHighlightId(id, parentNode)
                .forEach((element) => {
                    element.setAttribute(this.HIGHLIGHT_ID_ATTR, newId);
                });
        });
    },

    clearSelection() {
        if (document.selection) {
            document.selection.empty();
        } else if (window.getSelection) {
            window.getSelection().removeAllRanges();
        }
    },


    insertHighlightInDocument(range, id) {
        const classApplier = this.getClassApplierForStudentHighlights(id);
        classApplier.applyToRange(range);
    },

    hasValidSelection(range) {
        const stimulusElement = document.querySelector('[class*="stimulusContent"]');
        const isSelection = !!window.getSelection().toString() || !range.collapsed;
        const selectionInStimulus = (
            !!stimulusElement &&
            stimulusElement.contains(range.startContainer) &&
            stimulusElement.contains(range.endContainer)
        );
        return isSelection && selectionInStimulus;
    },

    updateToolTipBasedOnCurrentSelection() {
        const store = storeManager.getStore();
        const range = this.createValidRangeFromCurrentSelection();

        if (!range) {
            return false;
        }
        const hasValidSelection = this.hasValidSelection(range);
        store.dispatch(
            studentHighlightsActions.setHasValidSelection(hasValidSelection)
        );

        const classApplier = this.getClassApplierForStudentHighlights();
        return store.dispatch(
            studentHighlightsActions.setIsSelectionInHighlight(
                classApplier.isAppliedToSelection()
            )
        );
    },

    closeTooltip() {
        const store = storeManager.getStore();
        store.dispatch(
            studentHighlightsActions.setHasValidSelection(false)
        );
    },


    getClassApplierForStudentHighlights(highlightId) {
        const rangy = rangyService.getRangy();
        const options = {};

        if (highlightId !== undefined) {
            options.elementAttributes = {
                [this.HIGHLIGHT_ID_ATTR]: highlightId,
            };
        }
        return rangy.createClassApplier(this.STUDENT_HIGHLIGHT_CLASS_NAME, options);
    },

    applyStudentHighlights(sectionStudentHighlights, contentRevisionId) {
        const stimulusNode = this.getStimulusElement(contentRevisionId);
        sectionStudentHighlights.forEach((highlight) => {
            try {
                const range = toRange(highlight.start, highlight.startOffset, highlight.end, highlight.endOffset, stimulusNode);
                const rangyRange = this.getRangeLimitedToRoot(range);
                this.insertHighlightInDocument(rangyRange, highlight.id);
            } catch (e) {
                console.error(e.message);
            }
        });
    },

    addStudentHighlight() {
        const highlightId = uuid.v4();
        const { contentRevisionId } = this.getStimulusAndStudentHighlightInfo();
        const stimulusNode = this.getStimulusElement(contentRevisionId);

        const selectedRange = this.createValidRangeFromCurrentSelection(stimulusNode);
        if (!contentRevisionId || !selectedRange || selectedRange.collapsed) {
            return false;
        }

        const idMap = this.getAllHighlightElements(stimulusNode)
            .reduce((map, element) => {
                if (selectedRange.intersectsNode(element, true)) {
                    const id = element.getAttribute(this.HIGHLIGHT_ID_ATTR);
                    map[id] = true;
                }
                return map;
            }, {});

        const idsToReplace = Object.keys(idMap);
        this.insertHighlightInDocument(selectedRange, highlightId);
        this.replaceHighlightIdsInElements(highlightId, idsToReplace, stimulusNode);

        const highlightElements = this.getAllElementsForHighlightId(highlightId, stimulusNode);
        if (highlightElements.length > 1) {
            this.mergeElements(highlightElements);
        }

        const newHighlights = this.getAllHighlightRanges(stimulusNode);
        responseService.storeStudentHighlights(contentRevisionId, newHighlights);

        this.clearSelection();
        return this.closeTooltip();
    },

    removeHighlight() {
        const { contentRevisionId } = this.getStimulusAndStudentHighlightInfo();
        const stimulusNode = this.getStimulusElement(contentRevisionId);
        const range = this.createValidRangeFromCurrentSelection();
        const classApplier = this.getClassApplierForStudentHighlights();

        if (range && classApplier.isAppliedToSelection) {
            classApplier.undoToRange(range);
            this.cleanUpUnHighlightedSpans(stimulusNode);
        }

        if (contentRevisionId) {
            const newHighlights = this.getAllHighlightRanges(stimulusNode);
            responseService.storeStudentHighlights(contentRevisionId, newHighlights);
        }
        this.clearSelection();
        this.closeTooltip();
    },

    isNoteTakingAndHighlightingEnabled() {
        return !sessionReader.isAnnotationMode() &&
            marketplacePropertiesService.isAnnotatingEnabled() &&
            settingsReader.isCandidateHighlightingEnabled() &&
            this.isAnnotatablePage();
    },

    isAnnotatablePage() {
        return (sessionReader.isContextExam() || sessionReader.isContextPreview()) &&
            this.isAnnotatableRoute() &&
            sessionReader.isViewMediaScreen() &&
            !sessionReader.isParentTestContextOutsideChildTest();
    },

    isAnnotatableRoute() {
        const { currentlyShown: { routeType } } = storeManager.getState();
        return [
            routeTypes.ROUTE_TYPE_QUESTION,
            routeTypes.ROUTE_TYPE_STIMULUS,
            routeTypes.ROUTE_TYPE_SECTION,
        ].includes(routeType);
    },
};

export default studentHighlightsService;
